function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import EventEmitter from 'eventemitter3';
import { log, removeObserver } from '@netless/forge-room';
import { WhiteboardPermissionFlag } from '../WhitePermissions';
import { WhiteboardKeys } from '../WhiteboardKeys';
export class PageModel extends EventEmitter {
  constructor(whiteboardAttrsMap, userManager, requestUserMap, pageMap, hasPermission) {
    super();
    _defineProperty(this, "whiteboardAttrsMap", void 0);
    _defineProperty(this, "pageMap", void 0);
    _defineProperty(this, "requestUserMap", void 0);
    _defineProperty(this, "userManager", void 0);
    _defineProperty(this, "observers", new Map());
    _defineProperty(this, "hasPermission", void 0);
    _defineProperty(this, "handleUserLeave", user => {
      if (this.userManager.selfId === user.id) {
        return;
      }
      const cb = this.observers.get(user.id);
      if (cb) {
        removeObserver(this.requestUserMap(user.id), cb);
      }
    });
    _defineProperty(this, "handleUserJoin", user => {
      this.createCurrentPageObserver(user.id);
    });
    _defineProperty(this, "handleMainPageChange", evt => {
      for (const [key, value] of evt.changes.keys.entries()) {
        if (key === WhiteboardKeys.currentPage) {
          const cameraMode = this.requestUserMap(this.userManager.selfId).get(WhiteboardKeys.cameraMode);
          if (value.action === 'update' || value.action === 'add') {
            if (cameraMode === 'main') {
              const targetPage = this.whiteboardAttrsMap.get(WhiteboardKeys.currentPage);
              if (!this.pageMap.has(targetPage) && targetPage !== '_i_') {
                log(`main page {${targetPage}} not found.`, {}, 'error');
                return;
              }
              this.requestUserMap(this.userManager.selfId).set(WhiteboardKeys.currentPage, targetPage);
              this.emit('switchPage', targetPage);
            }
          }
        }
      }
    });
    _defineProperty(this, "handleUserCurrentPageChange", (userId, evt) => {
      for (const [key, value] of evt.changes.keys.entries()) {
        if (key === WhiteboardKeys.currentPage) {
          const cameraMode = this.requestUserMap(this.userManager.selfId).get(WhiteboardKeys.cameraMode);
          if (value.action === 'update' || value.action === 'add') {
            if (userId !== this.userManager.selfId && cameraMode === userId) {
              const targetPage = this.requestUserMap(userId).get(WhiteboardKeys.currentPage);
              this.requestUserMap(this.userManager.selfId).set(WhiteboardKeys.currentPage, targetPage);
              this.emit('switchPage', targetPage);
            } else if (userId === this.userManager.selfId && cameraMode === 'free') {
              const targetPage = this.requestUserMap(userId).get(WhiteboardKeys.currentPage);
              this.emit('switchPage', targetPage);
            }
          }
        }
      }
    });
    _defineProperty(this, "handlePageMapChange", () => {
      this.emit('pagesChange', Array.from(this.pageMap.keys()));
    });
    this.hasPermission = hasPermission;
    this.pageMap = pageMap;
    this.userManager = userManager;
    this.requestUserMap = requestUserMap;
    this.whiteboardAttrsMap = whiteboardAttrsMap;
    new Set(this.userManager.userIdList().concat(this.userManager.selfId)).forEach(userId => {
      this.createCurrentPageObserver(userId);
    });
    this.userManager.on('join', this.handleUserJoin);
    this.userManager.on('leave', this.handleUserLeave);
    this.whiteboardAttrsMap.observe(this.handleMainPageChange);
    this.pageMap.observe(this.handlePageMapChange);
  }
  createCurrentPageObserver(userId) {
    if (this.observers.has(userId)) {
      const cb = this.observers.get(userId);
      if (cb) {
        removeObserver(this.requestUserMap(userId), cb);
        this.observers.delete(userId);
      }
    }
    const observer = evt => {
      this.handleUserCurrentPageChange(userId, evt);
    };
    this.requestUserMap(userId).observe(observer);
    this.observers.set(userId, observer);
  }
  getCurrentPage(userId) {
    const cameraMode = this.requestUserMap(userId).get(WhiteboardKeys.cameraMode);
    if (cameraMode === 'main') {
      return this.whiteboardAttrsMap.get(WhiteboardKeys.currentPage) ?? null;
    } else if (cameraMode === 'free') {
      return this.requestUserMap(userId).get(WhiteboardKeys.currentPage) ?? null;
    } else {
      return this.requestUserMap(cameraMode).get(WhiteboardKeys.currentPage) ?? null;
    }
  }
  pageList() {
    return Array.from(this.pageMap.keys());
  }
  modifyDetect(id) {
    if (id === '_i_') {
      return true;
    }
    const cameraMode = this.requestUserMap(this.userManager.selfId).get(WhiteboardKeys.cameraMode);
    if (cameraMode !== 'main') {
      console.warn('cannot modify pages in non-main view mode.');
      return false;
    }
    if (!this.hasPermission(WhiteboardPermissionFlag.mainView)) {
      console.warn('cannot modify pages without WhiteboardPermissionFlag.mainView.');
      return false;
    }
    return true;
  }
  addPage(id) {
    let focus = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    if (!focus && !this.modifyDetect(id)) {
      return false;
    }
    if (this.pageMap.has(id)) {
      return false;
    }
    this.pageMap.set(id, '');
    return true;
  }
  deletePage(id, replace) {
    if (!this.modifyDetect('')) {
      return;
    }
    this.pageMap.delete(id);
    if (replace) {
      const mainPage = this.whiteboardAttrsMap.get(WhiteboardKeys.currentPage);
      if (mainPage === id) {
        this.whiteboardAttrsMap.set(WhiteboardKeys.currentPage, replace);
      }
    }
  }
  setUserPage(id, userId) {
    if (this.pageList().indexOf(id) < 0 || !this.hasPermission(WhiteboardPermissionFlag.setOthersView)) {
      return;
    }
    const userMap = this.requestUserMap(userId);
    const cameraMode = userMap.get(WhiteboardKeys.cameraMode);
    if (cameraMode === 'free') {
      userMap.set(WhiteboardKeys.currentPage, id);
    }
  }
  gotoPage(id) {
    if (this.pageList().indexOf(id) < 0) {
      return;
    }
    const userMap = this.requestUserMap(this.userManager.selfId);
    const cameraMode = userMap.get(WhiteboardKeys.cameraMode);
    if (cameraMode === 'free' || cameraMode === 'main') {
      userMap.set(WhiteboardKeys.currentPage, id);
    }
    if (cameraMode === 'main') {
      // 拥有主视角权限才能修改
      if (this.hasPermission(WhiteboardPermissionFlag.mainView)) {
        this.whiteboardAttrsMap.set(WhiteboardKeys.currentPage, id);
      }
    }
  }
  dispose() {
    for (const entry of this.observers.entries()) {
      const [userId, observer] = entry;
      removeObserver(this.requestUserMap(userId), observer);
    }
    this.userManager.off('join', this.handleUserJoin);
    this.userManager.off('leave', this.handleUserLeave);
    removeObserver(this.whiteboardAttrsMap, this.handleMainPageChange);
    removeObserver(this.pageMap, this.handlePageMapChange);
  }
}